import PublicProfileTimelineComponent from 'components/publicProfile/timeline/PublicProfileTimelineComponent';
import HeadMetadata from 'components/shared/HeadMetadata';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { useI18n } from 'hooks/useI18n';
import {
  ManfrediteConfigurationPublicProfileViewsType,
  PublicManfredite
} from 'interfaces/public-manfredite.interface';
import { useRouter } from 'next/router';
import { useContext, useEffect } from 'react';
import ServiceEventTracking from 'services/service.event-tracking';
import UIPublicProfileService from 'services/ui/publicprofile/publicprofile.service';
import { APP_URLS } from 'utils/app-urls';
import PublicProfileClassic from './classic/PublicProfileClassic';
import PublicProfilePdfComponent from './pdf/PublicProfilePdfComponent';

type PublicProfileComponentProps = {
  type?: ManfrediteConfigurationPublicProfileViewsType;
  publicManfredite: PublicManfredite;
  slug?: string;
  isDefaultView: boolean;
  sendVisitTrackEvent?: boolean;
};

const PublicProfileComponent: React.FC<PublicProfileComponentProps> = ({
  type = 'timeline',
  publicManfredite,
  slug,
  isDefaultView,
  sendVisitTrackEvent = true
}) => {
  const { t } = useI18n('publicProfile');
  const { locale } = useRouter();
  const { user, loadingUser } = useContext(LoggedUserContext);

  const uiPublicManfredite = UIPublicProfileService.getManfrediteUIData(
    publicManfredite,
    locale
  );

  const { title, desc, avatarUrl } =
    UIPublicProfileService.getManfrediteProfileMetadataInfo(
      uiPublicManfredite,
      t
    );

  const profileUrlPathToView = isDefaultView ? '' : '/' + type;
  const profileUrlPath = `${slug}${profileUrlPathToView}`;

  useEffect(() => {
    if (loadingUser) {
      return;
    }

    if (
      sendVisitTrackEvent &&
      slug &&
      (!user || user.configuration?.publicProfileSlug !== slug)
    ) {
      ServiceEventTracking.track({
        type: 'Website - Public Profile Viewed',
        properties: {
          url: window.location.href,
          title,
          slug,
          type
        }
      });
    }
  }, [loadingUser]);

  return (
    <>
      {slug && (
        <HeadMetadata
          title={title}
          desc={desc}
          imgUrl={avatarUrl}
          url={APP_URLS.manfrediteProfileV2}
          path={{
            ES: profileUrlPath,
            EN: profileUrlPath
          }}
        />
      )}

      {(!type || type === 'timeline') && (
        <PublicProfileTimelineComponent publicManfredite={uiPublicManfredite} />
      )}

      {type === 'classic' && (
        <PublicProfileClassic manfredite={uiPublicManfredite} />
      )}

      {type === 'pdf' && (
        <PublicProfilePdfComponent
          publicManfredite={uiPublicManfredite}
          slug={slug}
        />
      )}
    </>
  );
};

export default PublicProfileComponent;
